import { useCallback } from 'react'

import { Currency, extractValidationErrors, useMutation } from '@sequencehq/api'
import { dashboardv99990101Client } from '@sequencehq/api/dashboard/v99990101'
import { useNotifications } from '@sequencehq/core-components'

/**
 * Records a manual payment against an invoice via POST /payments. The endpoint
 * requires an invoiceBalanceVersion for optimistic concurrency; the caller
 * supplies the invoice's current ledger balance version (a fresh invoice is at
 * version 0). The caller passes a stable idempotencyKey so a retry after an
 * ambiguous failure dedupes rather than recording a second payment.
 */
export const useRecordPayment = ({ invoiceId }: { invoiceId: string }) => {
  const { displayNotification } = useNotifications()
  const { mutateAsync: postPayment } = useMutation(
    dashboardv99990101Client.postPayment
  )

  const recordPayment = useCallback(
    async ({
      amount,
      currency,
      paymentDate,
      invoiceBalanceVersion,
      idempotencyKey,
      reference
    }: {
      amount: string
      currency: Currency
      paymentDate: string
      invoiceBalanceVersion: number
      idempotencyKey?: string
      reference?: string
    }): Promise<boolean> => {
      try {
        await postPayment({
          idempotencyKey,
          body: {
            invoiceId,
            amount,
            currency,
            paymentDate,
            invoiceBalanceVersion,
            reference
          }
        })

        displayNotification('Payment recorded', { type: 'success' })

        return true
      } catch (error) {
        const [firstValidationError] = extractValidationErrors(error)
        displayNotification('Failed to record payment', {
          type: 'error',
          description: firstValidationError
        })

        return false
      }
    },
    [invoiceId, postPayment, displayNotification]
  )

  return { recordPayment }
}
